import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api, saveSession, getStoredToken, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import LangToggle from '@/components/LangToggle';

const FIELDS = [
  { key: 'age', type: 'number', tk: 'onb.age' },
  { key: 'height_cm', type: 'number', tk: 'onb.height' },
  { key: 'weight_kg', type: 'number', tk: 'onb.weight' },
  { key: 'target_weight_kg', type: 'number', tk: 'onb.target' },
  { key: 'goal', type: 'text', tk: 'onb.goal' },
  { key: 'training_experience', type: 'text', tk: 'onb.experience' },
  { key: 'injuries', type: 'text', tk: 'onb.injuries' },
  { key: 'complaints', type: 'text', tk: 'onb.complaints' },
  { key: 'nutrition_situation', type: 'text', tk: 'onb.nutrition' },
  { key: 'allergies', type: 'text', tk: 'onb.allergies' },
  { key: 'job_lifestyle', type: 'text', tk: 'onb.job' },
  { key: 'sleep', type: 'text', tk: 'onb.sleep' },
  { key: 'stress_level', type: 'text', tk: 'onb.stress', level: true },
  { key: 'activity_level', type: 'text', tk: 'onb.activity', level: true },
];
const REQUIRED = ['age', 'height_cm', 'weight_kg', 'goal'];

export default function Onboarding() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (loading) return;
    setError('');
    const missing = REQUIRED.filter((k) => {
      const v = data[k];
      return v === undefined || v === null || String(v).trim() === '';
    });
    if (missing.length) {
      const labels = missing.map((k) => t(FIELDS.find((f) => f.key === k).tk));
      setError(`${t('common.fillAll')}: ${labels.join(', ')}`);
      return;
    }
    // Scale fields (stress/activity): whole number 1–10, no silent correction.
    const badLevel = FIELDS.some((f) => {
      if (!f.level) return false;
      const raw = data[f.key];
      if (raw === undefined || raw === null || String(raw).trim() === '') return false;
      const s = String(raw).trim();
      return !/^\d+$/.test(s) || Number(s) < 1 || Number(s) > 10;
    });
    if (badLevel) {
      setError(t('onb.levelError'));
      return;
    }
    setLoading(true);
    try {
      const payload = {};
      for (const f of FIELDS) {
        const v = data[f.key];
        if (v === undefined || v === null || String(v).trim() === '') continue;
        payload[f.key] = f.type === 'number' ? parseFloat(String(v).replace(',', '.')) : String(v).trim();
      }
      const res = await api.post('/onboarding', { ...payload, lang });
      const fresh = res?.data?.user;
      const tk = getStoredToken();
      if (tk && fresh) saveSession(tk, fresh);
      else {
        const me = await api.get('/auth/me');
        if (tk && me?.data) saveSession(tk, me.data);
      }
      nav('/app/home', { replace: true });
    } catch (err) {
      setError(errMsg(err, t('common.saveFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-asgym-bg">
      <div className="mx-auto w-full max-w-[480px] px-6 py-10">
        <div className="mb-5 flex justify-end">
          <LangToggle />
        </div>
        <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white">{t('onb.title')}</h1>
        <p className="mb-8 mt-3 text-sm text-asgym-sub">{t('onb.sub')}</p>

        <div className="space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="as-label mb-2 block">
                {t(f.tk)}
                {f.level && <span className="ml-1.5 font-semibold tracking-normal normal-case opacity-60">(1–10)</span>}
              </label>
              <input
                data-testid={`onb-${f.key}`}
                className="as-input"
                inputMode={f.level ? 'numeric' : f.type === 'number' ? 'decimal' : 'text'}
                {...(f.level ? { pattern: '[0-9]*', maxLength: 2, placeholder: '1–10' } : {})}
                value={data[f.key] || ''}
                disabled={loading}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
              />
            </div>
          ))}
        </div>

        {error && (
          <div data-testid="onb-error" className="mt-5 rounded-xl border border-asgym-error/50 bg-asgym-error/10 p-4 text-[13px] font-bold text-asgym-error">
            {error}
          </div>
        )}

        <button data-testid="onb-submit" disabled={loading} onClick={submit} className="as-btn-primary mt-6 mb-10 w-full">
          {loading ? <Loader2 className="animate-spin" size={20} /> : t('onb.submit')}
        </button>
      </div>
    </div>
  );
}
